(function (root, factory) {
  const api = factory(root);

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.BakerraePickupCalendar = api;
  }
})(typeof window !== 'undefined' ? window : undefined, function (root) {
  const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const MINUTE_MS = 60 * 1000;
  const HOUR_MS = 60 * MINUTE_MS;
  const DAY_MS = 24 * HOUR_MS;
  const formatterCache = new Map();

  function safeJsonParse(value, fallback) {
    if (Array.isArray(value) || (value && typeof value === 'object')) return value;
    if (typeof value !== 'string' || value.trim() === '') return fallback;

    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  function toNumber(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function parseDate(dateString) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString || '');
    if (!match) return null;

    return {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
    };
  }

  function pad2(value) {
    return String(value).padStart(2, '0');
  }

  function dateToString(parts) {
    return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
  }

  function dateToOrdinal(dateString) {
    const parts = parseDate(dateString);
    if (!parts) return NaN;
    return Math.floor(Date.UTC(parts.year, parts.month - 1, parts.day) / DAY_MS);
  }

  function ordinalToDate(ordinal) {
    const date = new Date(ordinal * DAY_MS);
    return dateToString({
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
    });
  }

  function addDays(dateString, days) {
    return ordinalToDate(dateToOrdinal(dateString) + days);
  }

  function getFormatter(timezone) {
    if (!formatterCache.has(timezone)) {
      formatterCache.set(
        timezone,
        new Intl.DateTimeFormat('en-CA', {
          timeZone: timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23',
        })
      );
    }

    return formatterCache.get(timezone);
  }

  function getZonedParts(date, timezone) {
    const values = {};
    getFormatter(timezone)
      .formatToParts(date)
      .forEach((part) => {
        if (part.type !== 'literal') values[part.type] = Number(part.value);
      });

    return values;
  }

  function getTodayInZone(timezone, nowMs) {
    const parts = getZonedParts(new Date(nowMs || Date.now()), timezone);
    return dateToString(parts);
  }

  function getTimeZoneOffsetMs(timestamp, timezone) {
    const parts = getZonedParts(new Date(timestamp), timezone);
    const zonedAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    return zonedAsUtc - timestamp;
  }

  function timeToMinutes(timeString) {
    const match = /^(\d{1,2}):(\d{2})$/.exec(timeString || '');
    if (!match) return NaN;

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return NaN;

    return hours * 60 + minutes;
  }

  function minutesToTime(minutes) {
    return `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`;
  }

  function zonedLocalToUtcMs(dateString, timeString, timezone) {
    const parts = parseDate(dateString);
    const minutes = timeToMinutes(timeString);
    if (!parts || !Number.isFinite(minutes)) return NaN;

    const hour = Math.floor(minutes / 60);
    const minute = minutes % 60;
    const guess = Date.UTC(parts.year, parts.month - 1, parts.day, hour, minute, 0);
    let offset = getTimeZoneOffsetMs(guess, timezone);
    let utc = guess - offset;
    const secondOffset = getTimeZoneOffsetMs(utc, timezone);

    if (secondOffset !== offset) {
      utc = guess - secondOffset;
    }

    return utc;
  }

  function zonedLocalExists(dateString, timeString, timezone, utcMs) {
    if (!Number.isFinite(utcMs)) return false;

    const dateParts = parseDate(dateString);
    const minutes = timeToMinutes(timeString);
    const zonedParts = getZonedParts(new Date(utcMs), timezone);

    return (
      dateParts &&
      Number.isFinite(minutes) &&
      zonedParts.year === dateParts.year &&
      zonedParts.month === dateParts.month &&
      zonedParts.day === dateParts.day &&
      zonedParts.hour === Math.floor(minutes / 60) &&
      zonedParts.minute === minutes % 60
    );
  }

  function getDayName(dateString) {
    const parts = parseDate(dateString);
    if (!parts) return '';
    return DAY_NAMES[new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay()];
  }

  function formatDisplayDate(dateString) {
    const parts = parseDate(dateString);
    if (!parts) return dateString;

    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'UTC',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(Date.UTC(parts.year, parts.month - 1, parts.day)));
  }

  function formatDisplayTime(timeString) {
    const minutes = timeToMinutes(timeString);
    if (!Number.isFinite(minutes)) return timeString;

    const hours24 = Math.floor(minutes / 60);
    const suffix = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 % 12 || 12;
    return `${hours12}:${pad2(minutes % 60)} ${suffix}`;
  }

  function normalizeCsv(value) {
    return String(value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function ruleApplies(rule, product) {
    if (rule.appliesToAllPickupProducts !== false) return true;

    const productHandles = normalizeCsv(rule.productHandlesCsv);
    if (productHandles.includes(product.handle)) return true;

    const collectionHandles = normalizeCsv(rule.collectionHandlesCsv);
    return (product.collectionHandles || []).some((handle) => collectionHandles.includes(handle));
  }

  function normalizeWindows(value) {
    const windows = safeJsonParse(value, []);
    if (!Array.isArray(windows)) return [];

    return windows
      .map((window) => {
        if (!Array.isArray(window) || window.length < 2) return null;
        const start = timeToMinutes(window[0]);
        const end = timeToMinutes(window[1]);
        if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
        return [start, end];
      })
      .filter(Boolean);
  }

  function normalizeRules(rawRules) {
    const settings = rawRules.settings || {};
    const product = rawRules.product || {};
    const timezone = settings.timezone || 'America/Toronto';

    return {
      product: {
        handle: product.handle || '',
        required: product.required === true || product.required === 'true',
        leadTimeHours: toNumber(product.leadTimeHours, toNumber(settings.defaultLeadTimeHours, 72)),
        maxDaysAhead: toNumber(product.maxDaysAhead, toNumber(settings.defaultMaxDaysAhead, 90)),
        slotIntervalMinutes: toNumber(product.slotIntervalMinutes, toNumber(settings.defaultSlotIntervalMinutes, 30)),
        collectionHandles: Array.isArray(product.collectionHandles) ? product.collectionHandles : [],
      },
      settings: {
        timezone,
        weeklyHours: safeJsonParse(settings.weeklyHoursJson, {}),
        rulesVersion: settings.rulesVersion || '2026-06-17-v1',
        pickupLocation: settings.pickupLocation || 'Bakerrae',
      },
      blackouts: Array.isArray(rawRules.blackouts)
        ? rawRules.blackouts.filter((rule) => rule.active !== false && rule.active !== 'false')
        : [],
      specialDays: Array.isArray(rawRules.specialDays)
        ? rawRules.specialDays.filter((rule) => rule.active !== false && rule.active !== 'false')
        : [],
    };
  }

  function getSpecialDay(rules, dateString) {
    return rules.specialDays.find((specialDay) => specialDay.date === dateString && ruleApplies(specialDay, rules.product));
  }

  function getBlackouts(rules, dateString) {
    const selectedOrdinal = dateToOrdinal(dateString);

    return rules.blackouts.filter((blackout) => {
      if (!ruleApplies(blackout, rules.product)) return false;

      const start = dateToOrdinal(blackout.startDate);
      const end = dateToOrdinal(blackout.endDate || blackout.startDate);
      return Number.isFinite(start) && Number.isFinite(end) && selectedOrdinal >= start && selectedOrdinal <= end;
    });
  }

  function subtractBlockedWindows(slots, blockedWindows) {
    if (!blockedWindows.length) return slots;

    return slots.filter((slot) => {
      const slotMinutes = timeToMinutes(slot.time);
      return !blockedWindows.some(([start, end]) => slotMinutes >= start && slotMinutes < end);
    });
  }

  function buildSlotsForDate(rawRules, dateString, nowMs) {
    const rules = rawRules.settings ? rawRules : normalizeRules(rawRules);
    const today = getTodayInZone(rules.settings.timezone, nowMs);
    const dateOffset = dateToOrdinal(dateString) - dateToOrdinal(today);

    if (dateOffset < 0 || dateOffset > rules.product.maxDaysAhead) return [];

    const blackouts = getBlackouts(rules, dateString);
    if (blackouts.some((blackout) => blackout.allDay === true || blackout.allDay === 'true')) return [];

    const specialDay = getSpecialDay(rules, dateString);
    const dayName = getDayName(dateString);
    const openWindows = specialDay
      ? normalizeWindows(specialDay.openWindowsJson)
      : normalizeWindows(rules.settings.weeklyHours[dayName] || []);

    const leadTimeHours = specialDay && specialDay.leadTimeHoursOverride
      ? toNumber(specialDay.leadTimeHoursOverride, rules.product.leadTimeHours)
      : rules.product.leadTimeHours;
    const earliestUtc = (nowMs || Date.now()) + leadTimeHours * HOUR_MS;
    const interval = Math.max(5, rules.product.slotIntervalMinutes);
    const slots = [];

    openWindows.forEach(([start, end]) => {
      for (let minute = start; minute + interval <= end; minute += interval) {
        const time = minutesToTime(minute);
        const utcMs = zonedLocalToUtcMs(dateString, time, rules.settings.timezone);
        if (zonedLocalExists(dateString, time, rules.settings.timezone, utcMs) && utcMs >= earliestUtc) {
          slots.push({
            date: dateString,
            time,
            iso: new Date(utcMs).toISOString(),
            label: formatDisplayTime(time),
          });
        }
      }
    });

    const blockedWindows = blackouts.flatMap((blackout) => normalizeWindows(blackout.blockedWindowsJson));
    return subtractBlockedWindows(slots, blockedWindows);
  }

  function cartJsonUrl() {
    const cartUrl = (root && root.routes && root.routes.cart_url) || '/cart';
    return `${cartUrl}.js`;
  }

  function getProperty(properties, name) {
    if (!properties) return '';

    if (Array.isArray(properties)) {
      const match = properties.find((property) => property && property[0] === name);
      return match ? match[1] : '';
    }

    return properties[name] || '';
  }

  function cartHasPickupItems(cart) {
    const summary = root && root.document && root.document.querySelector('[data-pickup-cart-summary]');
    if (summary && summary.dataset.pickupRequired === 'true') return true;

    return (cart.items || []).some((item) => {
      if (String(item.product_id) === '15945515467038') return false;
      if (getProperty(item.properties, '_pickup_iso')) return true;
      if (getProperty(item.properties, 'Pickup Date') && getProperty(item.properties, 'Pickup Time')) return true;
      return String(item.product_type || '').toLowerCase().includes('cake');
    });
  }

  function extractCartPickup(cart) {
    const attributes = cart.attributes || {};
    const pickup = {
      date: attributes['Pickup Date'] || '',
      time: attributes['Pickup Time'] || '',
      location: attributes['Pickup Location'] || '',
      iso: attributes['Pickup Datetime'] || '',
      timezone: attributes['Pickup Timezone'] || '',
      rulesVersion: attributes['Pickup Rules Version'] || '',
      hasPickupItems: cartHasPickupItems(cart),
    };

    if ((!pickup.date || !pickup.time || !pickup.iso) && pickup.hasPickupItems) {
      const pickupItem = (cart.items || []).find((item) => String(item.product_id) !== '15945515467038' && getProperty(item.properties, '_pickup_iso'));
      if (pickupItem) {
        pickup.date = pickup.date || getProperty(pickupItem.properties, 'Pickup Date');
        pickup.time = pickup.time || getProperty(pickupItem.properties, 'Pickup Time');
        pickup.location = pickup.location || getProperty(pickupItem.properties, 'Pickup Location');
        pickup.iso = pickup.iso || getProperty(pickupItem.properties, '_pickup_iso');
        pickup.timezone = pickup.timezone || getProperty(pickupItem.properties, '_pickup_timezone');
        pickup.rulesVersion = pickup.rulesVersion || getProperty(pickupItem.properties, '_pickup_rule_version');
      }
    }

    return pickup;
  }

  function fetchCart() {
    return fetch(cartJsonUrl(), { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error('Cart unavailable');
        return response.json();
      })
      .then((cart) => {
        if (!Array.isArray(cart.items)) throw new Error('Cart unavailable');
        return cart;
      });
  }

  function updateCartAttributes(attributes) {
    const url = (root && root.routes && root.routes.cart_update_url) || '/cart/update.js';

    return fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ attributes }),
    }).then((response) => response.json());
  }

  function getCurrentCartRulesVersion() {
    const summary = root && root.document && root.document.querySelector('[data-pickup-cart-summary]');
    return summary ? summary.dataset.pickupRulesVersion || '' : '';
  }

  function buildSelection(rules, slot) {
    if (!slot) return null;

    return {
      date: slot.date,
      dateLabel: formatDisplayDate(slot.date),
      time: slot.time,
      timeLabel: formatDisplayTime(slot.time),
      iso: slot.iso,
      timezone: rules.settings.timezone,
      location: rules.settings.pickupLocation,
      rulesVersion: rules.settings.rulesVersion,
    };
  }

  function getPickerForForm(form) {
    return form ? form.querySelector('pickup-date-time-picker[data-pickup-required="true"]') : null;
  }

  function syncCartAttributesForForm(form) {
    const picker = getPickerForForm(form);
    if (!picker || typeof picker.getSelection !== 'function') return Promise.resolve();

    const selection = picker.getSelection();
    if (!selection) return Promise.reject(new Error('Pickup date and time are required.'));

    return updateCartAttributes({
      'Pickup Date': selection.date,
      'Pickup Time': selection.timeLabel,
      'Pickup Location': selection.location,
      'Pickup Datetime': selection.iso,
      'Pickup Timezone': selection.timezone,
      'Order Type': 'Pickup',
      'Pickup Rules Version': selection.rulesVersion,
    });
  }

  function clearPickupCartAttributes() {
    return updateCartAttributes({
      'Pickup Date': '',
      'Pickup Time': '',
      'Pickup Location': '',
      'Pickup Datetime': '',
      'Pickup Timezone': '',
      'Order Type': '',
      'Pickup Rules Version': '',
    });
  }

  function validatePickupCart(cart) {
    const piePickup = root && root.BakerraePiePickup;
    const hasPies = (cart.items || []).some((item) => String(item.product_id) === '15945515467038');
    if (hasPies && (!piePickup || !piePickup.validateCart)) {
      return Promise.resolve({ ok: false, message: 'Pie pickup could not be validated. Please refresh the cart and try again.' });
    }
    if (piePickup && piePickup.hasUnsavedCartSelection && piePickup.hasUnsavedCartSelection()) {
      return Promise.resolve({ ok: false, message: 'Save the updated pickup details for your Thanksgiving pies before checkout.' });
    }
    if (piePickup) {
      const result = piePickup.applyCart ? piePickup.applyCart(cart) : piePickup.validateCart(cart);
      if (!result.ok) return Promise.resolve(result);
    }
    return validateCakePickupCart(cart);
  }

  function validateCakePickupCart(cart) {
    const pickup = extractCartPickup(cart);
    if (!pickup.hasPickupItems) {
      const attributes = cart.attributes || {};
      const hasStaleAttributes = Boolean(attributes['Pickup Date'] || attributes['Pickup Time'] || attributes['Pickup Datetime']);
      return hasStaleAttributes
        ? clearPickupCartAttributes().then(() => ({ ok: true }))
        : Promise.resolve({ ok: true });
    }

    if (!pickup.date || !pickup.time || !pickup.location || !pickup.iso || !pickup.timezone || !pickup.rulesVersion) {
      return Promise.resolve({
        ok: false,
        message: 'Pickup details are missing. Return to the cake product page and choose a pickup date and time.',
      });
    }

    const currentRulesVersion = getCurrentCartRulesVersion();
    if (currentRulesVersion && pickup.rulesVersion !== currentRulesVersion) {
      return Promise.resolve({
        ok: false,
        message: 'Pickup availability has changed. Return to the cake product page and choose a new pickup time.',
      });
    }

    if (!Number.isFinite(Date.parse(pickup.iso)) || Date.parse(pickup.iso) <= Date.now()) {
      return Promise.resolve({
        ok: false,
        message: 'This pickup time is no longer available. Return to the cake product page and choose a new pickup time.',
      });
    }

    const pickupItems = (cart.items || []).filter((item) => {
      return String(item.product_id) !== '15945515467038' && (getProperty(item.properties, '_pickup_iso') || String(item.product_type || '').toLowerCase().includes('cake'));
    });

    const mismatchedItem = pickupItems.find((item) => {
      const itemIso = getProperty(item.properties, '_pickup_iso');
      return itemIso && itemIso !== pickup.iso;
    });

    if (mismatchedItem) {
      return Promise.resolve({
        ok: false,
        message: 'All pickup items in one checkout must use the same pickup date and time.',
      });
    }

    return Promise.resolve({ ok: true });
  }

  function showCheckoutError(message) {
    const errorElement = Array.from(root.document.querySelectorAll('[data-pie-checkout-error], [data-pickup-checkout-error]'))
      .find((element) => element.closest('cart-notification.active, .cart-notification.active, cart-drawer.active')) ||
      root.document.querySelector('[data-pie-checkout-error], [data-pickup-checkout-error]');
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.hidden = false;
      return;
    }

    const cartErrors = root.document.getElementById('cart-errors') || root.document.getElementById('CartDrawer-CartErrors');
    if (cartErrors) cartErrors.textContent = message;
  }

  function installCheckoutValidation() {
    if (!root || !root.document) return;

    root.document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        const submitter = event.submitter || root.document.activeElement;
        if (!form || !submitter || submitter.name !== 'checkout') return;
        if (form.dataset.pickupCheckoutValidated === 'true') {
          delete form.dataset.pickupCheckoutValidated;
          return;
        }

        event.preventDefault();
        if (form.dataset.pickupCheckoutPending === 'true') return;
        form.dataset.pickupCheckoutPending = 'true';
        submitter.setAttribute('aria-disabled', 'true');

        fetchCart()
          .then(validatePickupCart)
          .then((result) => {
            if (!result.ok) {
              showCheckoutError(result.message);
              submitter.removeAttribute('aria-disabled');
              return;
            }

            submitter.removeAttribute('aria-disabled');
            form.dataset.pickupCheckoutValidated = 'true';
            form.requestSubmit(submitter);
          })
          .catch(() => {
            showCheckoutError('Pickup details could not be validated. Please refresh the cart and try again.');
            submitter.removeAttribute('aria-disabled');
          })
          .finally(() => { delete form.dataset.pickupCheckoutPending; });
      },
      true
    );
  }

  function installCheckoutLinks() {
    if (!root || !root.document) return;
    root.document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link) return;
      const url = new URL(link.href, root.location.href);
      if (url.origin !== root.location.origin || !/\/checkouts?(?:\/|$)/.test(url.pathname)) return;
      event.preventDefault();
      fetchCart().then(validatePickupCart).then((result) => {
        if (result.ok) root.location.assign(link.href);
        else showCheckoutError(result.message);
      }).catch(() => showCheckoutError('Pickup details could not be validated. Please refresh the cart and try again.'));
    }, true);
  }

  if (root && root.HTMLElement) {
    class PickupDateTimePicker extends root.HTMLElement {
      connectedCallback() {
        this.form = this.closest('form');
        this.dateInput = this.querySelector('[data-pickup-date]');
        this.timeSelect = this.querySelector('[data-pickup-time]');
        this.message = this.querySelector('[data-pickup-message]');
        this.summary = this.querySelector('[data-pickup-summary]');
        this.submitButton = this.form && this.form.querySelector('[type="submit"][name="add"]');
        this.variantInput = this.form && this.form.querySelector('.product-variant-id');
        this.rules = this.readRules();
        this.slots = [];
        this.selectedSlot = null;
        this.cartPickup = null;
        this.submitWasStockDisabled = Boolean(this.variantInput && this.variantInput.disabled);

        if (!this.rules.product.required || !this.dateInput || !this.timeSelect || !this.form) return;

        if (this.submitButton) {
          this.submitButton.dataset.pickupDisabled = 'true';
        }

        this.setDateBounds();
        this.bindEvents();
        this.setSubmitState(false);
        this.loadCartPickup().finally(() => this.validate());
      }

      readRules() {
        const script = this.querySelector('[data-pickup-rules]');
        return normalizeRules(safeJsonParse(script ? script.textContent : '{}', {}));
      }

      bindEvents() {
        this.dateInput.addEventListener('change', () => {
          this.populateTimes();
          this.validate();
        });

        this.timeSelect.addEventListener('change', () => {
          this.validate();
        });

        this.form.addEventListener(
          'submit',
          (event) => {
            if (!this.validate()) {
              event.preventDefault();
              event.stopImmediatePropagation();
            }
          },
          true
        );

        this.form.addEventListener('change', () => {
          root.setTimeout(() => this.validate(), 0);
        });
      }

      loadCartPickup() {
        return fetchCart()
          .then((cart) => {
            const pickup = extractCartPickup(cart);
            this.cartPickup = pickup.hasPickupItems ? pickup : null;

            if (this.cartPickup && this.cartPickup.date && this.cartPickup.iso && !this.dateInput.value) {
              this.dateInput.value = this.cartPickup.date;
              this.populateTimes();
              const matchingOption = Array.from(this.timeSelect.options).find(
                (option) => option.dataset.iso === this.cartPickup.iso
              );
              if (matchingOption) this.timeSelect.value = matchingOption.value;
            }
          })
          .catch(() => {});
      }

      setDateBounds() {
        const today = getTodayInZone(this.rules.settings.timezone);
        this.dateInput.min = today;
        this.dateInput.max = addDays(today, this.rules.product.maxDaysAhead);
      }

      populateTimes() {
        this.selectedSlot = null;
        this.timeSelect.innerHTML = '';
        this.slots = buildSlotsForDate(this.rules, this.dateInput.value);

        if (!this.dateInput.value) {
          this.addTimeOption('', 'Select a date first');
          this.timeSelect.disabled = true;
          return;
        }

        if (!this.slots.length) {
          this.addTimeOption('', 'No pickup times available');
          this.timeSelect.disabled = true;
          return;
        }

        this.addTimeOption('', 'Select a pickup time');
        this.slots.forEach((slot) => {
          const option = this.addTimeOption(slot.time, slot.label);
          option.dataset.iso = slot.iso;
        });
        this.timeSelect.disabled = false;
      }

      addTimeOption(value, label) {
        const option = root.document.createElement('option');
        option.value = value;
        option.textContent = label;
        this.timeSelect.append(option);
        return option;
      }

      getSelection() {
        return this.selectedSlot ? buildSelection(this.rules, this.selectedSlot) : null;
      }

      validate() {
        const date = this.dateInput.value;
        const time = this.timeSelect.value;
        const slot = this.slots.find((availableSlot) => availableSlot.date === date && availableSlot.time === time);
        this.selectedSlot = slot || null;

        if (!date) {
          this.setMessage('Select a pickup date to see available times.');
          this.setSummary(null);
          this.setSubmitState(false);
          return false;
        }

        if (!this.slots.length) {
          this.setMessage('No pickup times are available for this date.', true);
          this.setSummary(null);
          this.setSubmitState(false);
          return false;
        }

        if (!slot) {
          this.setMessage('Select a pickup time.');
          this.setSummary(null);
          this.setSubmitState(false);
          return false;
        }

        const selection = buildSelection(this.rules, slot);
        if (this.cartPickup && this.cartPickup.iso && selection.iso !== this.cartPickup.iso) {
          this.setMessage(
            'This cart already has a pickup time. Use the same time or remove pickup items from the cart first.',
            true
          );
          this.setSummary(null);
          this.setSubmitState(false);
          return false;
        }

        this.writeProperties(selection);
        this.setMessage('');
        this.setSummary(selection);
        this.setSubmitState(true);
        return true;
      }

      setMessage(text, isError) {
        this.message.textContent = text;
        this.message.classList.toggle('pickup-calendar__message--error', Boolean(isError));
      }

      setSummary(selection) {
        if (!selection) {
          this.summary.hidden = true;
          this.summary.textContent = '';
          return;
        }

        this.summary.hidden = false;
        this.summary.textContent = `Pickup ${selection.dateLabel} at ${selection.timeLabel}.`;
      }

      setSubmitState(enabled) {
        if (!this.submitButton || this.submitWasStockDisabled) return;

        if (enabled) {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
        } else {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
        }
      }

      writeProperties(selection) {
        const values = {
          'Pickup Date': selection.date,
          'Pickup Time': selection.timeLabel,
          'Pickup Location': selection.location,
          _pickup_iso: selection.iso,
          _pickup_date: selection.date,
          _pickup_time_24h: selection.time,
          _pickup_timezone: selection.timezone,
          _pickup_rule_version: selection.rulesVersion,
        };

        Object.entries(values).forEach(([name, value]) => {
          const input = this.querySelector(`[data-pickup-property="${name}"]`);
          if (input) input.value = value;
        });
      }
    }

    if (root.customElements && !root.customElements.get('pickup-date-time-picker')) {
      root.customElements.define('pickup-date-time-picker', PickupDateTimePicker);
    }
  }

  if (root && root.document) {
    installCheckoutValidation();
    installCheckoutLinks();
  }

  return {
    syncCartAttributesForForm,
    utils: {
      addDays,
      buildSlotsForDate,
      dateToOrdinal,
      extractCartPickup,
      validatePickupCart,
      validateCakePickupCart,
      formatDisplayTime,
      getTodayInZone,
      normalizeRules,
      timeToMinutes,
      zonedLocalToUtcMs,
      zonedLocalExists,
    },
  };
});
